import { useEffect } from "react";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import { useGetUserSettings } from "src/views/users/SettingsService";
import { useChatStore } from "../stores/ChatStore";
import useAssistantNavigation from "./useAssistantNavigation";

const useTalkFirst = () => {
  const { navigateToChat } = useAssistantNavigation();

  const {
    config: chatConfig,
    persona,
    hasTalkedFirst,
    setHasTalkedFirst,
    forceAutoStart,
    setForceAutoStart,
    setPersonaForWorkflow,
    workflowPersona,
  } = useChatStore();

  const { data: userSettings } = useGetUserSettings();

  const autoStartSetting = Boolean(userSettings?.auto_start);
  // forceAutoStart from workflow navigation bypasses user's auto_start preference
  const shouldAutoStart = autoStartSetting || forceAutoStart;

  const {
    mutateAsync: createDialogue,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useCreateDialogue();

  const handleTalkFirst = async () => {
    const isChatReady = useChatStore.getState().isChatReady();

    if (!isChatReady || !chatConfig) {
      console.error("Chat is not ready. Please select a model.");
      return;
    }

    // Clear workflow state if this was triggered by a workflow click
    if (forceAutoStart) {
      setForceAutoStart(false);
      setPersonaForWorkflow(null);
    }

    // Always create dialogue WITH persona — the backend handles
    // unauthorized (license check) and unconfigured (missing resources)
    // prompts via apply_persona_system_prompt in converse_ops.py.
    const activeDatasetIds = useChatStore.getState().getActiveDatasetIds();

    await createDialogue(
      {
        conversation: {
          model_id: chatConfig.modelId ?? null,
          model_version_number: chatConfig.modelVersionNum ?? null,
          search_datasets: activeDatasetIds,
          model_inference_data: {
            infer_data: JSON.stringify([]),
            stream: true,
            streaming_response_required: true,
            text_gen_model_params: {},
          },
          tool_calling: true,
          chat_config_id: chatConfig.chatConfigId,
        },
      },
      {
        onSuccess: (dialogueId: number) => {
          setHasTalkedFirst(true);

          navigateToChat({
            dialogueId,
            options: {
              persona: persona?.slug ? persona.slug : undefined,
              config: chatConfig || undefined,
              userMessage: "",
              // The greeting locks the mode on arrival; keep the splash's choice.
              responseMode: useChatStore.getState().responseMode,
            },
          });
        },
      },
    );
  };

  useEffect(() => {
    // Wait for complete persona config before auto-start to prevent cutoff messages
    const hasPersonaInStore = Boolean(persona);

    // When forceAutoStart is true (workflow click), the persona is being resolved
    // from the URL ?persona=slug param by useSyncPersonaToStore. We MUST wait for
    // chatConfig to have chatConfigId & slug that MATCHES the requested workflow.
    // Without this slug check, stale config from a previous workflow would cause
    // handleTalkFirst to fire immediately with the wrong persona.
    const hasCompletePersonaConfig = forceAutoStart
      ? Boolean(
          chatConfig?.chatConfigId &&
          persona?.slug &&
          persona.slug === workflowPersona?.slug,
        )
      : !hasPersonaInStore ||
        Boolean(chatConfig?.chatConfigId && persona?.slug) ||
        Boolean(persona?.slug && !chatConfig?.chatConfigId);

    const currentChatReady = useChatStore.getState().isChatReady();

    if (
      !hasTalkedFirst &&
      shouldAutoStart &&
      currentChatReady &&
      hasCompletePersonaConfig && // CRITICAL: Wait for complete persona config
      !isLoading
    ) {
      handleTalkFirst();
    }
  }, [
    shouldAutoStart,
    hasTalkedFirst,
    isLoading,
    chatConfig?.chatConfigId,
    persona?.slug,
    forceAutoStart,
    workflowPersona,
  ]);

  return {
    isLoading,
    isError,
    isSuccess,
    error,
  };
};

export default useTalkFirst;
